import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { ItemsContext } from "../context/ItemsContext";
import { jwtDecode } from "jwt-decode";
import AOS from "aos";
import "aos/dist/aos.css";
import CategoriesList from "./CategoriesList";
import CategorySearchBar from "./CategorySearchBar";
import api from "../utils/api";
import { useAuth } from "./authentication/AuthContext";
import axios from "axios";

const globalProductCache = {};
const globalCategoryCache = { data: null, timestamp: 0 };

const Sidebar = ({ setSelectedCategory, setSearchQuery, navZone, navCategoryIndex, setNavCategoryIndex, setNavZone }) => {
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const { setItems } = useContext(ItemsContext);
  const { user } = useAuth();
  const userId = user?.id || "";
  const [activeCategory, setActiveCategory] = useState("All");

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarWidth, setSidebarWidth] = useState(210);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startResizing = (mouseDownEvent) => {
    mouseDownEvent.preventDefault();
    const startWidth = sidebarWidth;
    const startX = mouseDownEvent.clientX;

    const doDrag = (mouseMoveEvent) => {
      const newWidth = startWidth + (mouseMoveEvent.clientX - startX);
      if (newWidth >= 120 && newWidth <= 350) {
        setSidebarWidth(newWidth);
      }
    };

    const stopDrag = () => {
      document.removeEventListener("mousemove", doDrag);
      document.removeEventListener("mouseup", stopDrag);
    };

    document.addEventListener("mousemove", doDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      mirror: false,
    });
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      if (globalCategoryCache.data && (Date.now() - globalCategoryCache.timestamp < 300000)) {
        setCategories(globalCategoryCache.data);
        setFilteredCategories(globalCategoryCache.data);
        // Stale-while-revalidate for categories too (optional, but good for real-time updates)
      }

      try {
        const response = await api.get(`/newcategories/${userId}?status=Active&limit=1000`);
        const catData = response.data.data || [];
        
        globalCategoryCache.data = catData;
        globalCategoryCache.timestamp = Date.now();

        setCategories(catData);
        setFilteredCategories(catData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    if (userId) {
      fetchCategories();
    }
  }, [userId]);

  const productsAbortControllerRef = useRef(null);

  const fetchProducts = useCallback(async (category) => {
    // 1. Instantly load from cache if available (Stale-While-Revalidate)
    if (globalProductCache[category]) {
      setItems(globalProductCache[category]);
      setSearchQuery("");
    }

    if (productsAbortControllerRef.current) {
      productsAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    productsAbortControllerRef.current = abortController;

    try {
      const response = await api.get(`/products/${userId}`, {
        params: { category: category === "All" ? "all" : category, limit: 1000 },
        signal: abortController.signal
      });

      const products = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      // 2. Update cache
      globalProductCache[category] = products;

      // 3. Silently update state to reflect any new changes from server
      setItems(products);
      
      if (!globalProductCache[category]) {
        setSearchQuery("");
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError" || error.message === "canceled") {
        console.log("Products fetch API call aborted:", category);
      } else {
        console.error("Error fetching data:", error);
        if (!globalProductCache[category]) setItems([]);
      }
    }
  }, [userId, setItems, setSearchQuery]);

  useEffect(() => {
    if (!userId) return;
    setSelectedCategory("All");
    fetchProducts("All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 1. Instant local search filtering
  useEffect(() => {
    const query = categorySearchQuery.trim();
    if (query === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((category) =>
        category.newTitle.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [categorySearchQuery, categories]);

  // 2. Debounced background API search for categories from DB
  useEffect(() => {
    const query = categorySearchQuery.trim();
    if (query === "" || !userId) return;

    // Create an AbortController for this request
    const abortController = new AbortController();

    const delayDebounce = setTimeout(async () => {
      try {
        const response = await api.get(
          `/newcategories/${userId}?status=Active&search=${encodeURIComponent(query)}&limit=1000`,
          { signal: abortController.signal }
        );
        const serverResults = response.data.data || [];

        // Merge any new categories found on the server into the local state
        setCategories((prevCategories) => {
          const merged = [...prevCategories];
          let updated = false;

          serverResults.forEach((serverCat) => {
            if (!merged.some((c) => c._id === serverCat._id)) {
              merged.push(serverCat);
              updated = true;
            }
          });

          return updated ? merged : prevCategories;
        });
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError" || err.message === "canceled") {
          // Request was cancelled silently, no warning needed
          console.log("Category search API call aborted:", query);
        } else {
          console.error("Background category search failed:", err);
        }
      }
    }, 400);

    return () => {
      clearTimeout(delayDebounce);
      abortController.abort(); // Cancel the request if query changes or component unmounts
    };
  }, [categorySearchQuery, userId]);


  const handleCategorySelection = (categoryName) => {
    setSelectedCategory(categoryName);
    setActiveCategory(categoryName);

    setSearchQuery("");
    localStorage.setItem("selectedCategory", categoryName);
      fetchProducts(categoryName);
    setCategorySearchQuery("");
  };



  return (
    <div
      className="flex flex-col relative bg-white py-5 rounded-2xl border border-slate-100 shadow-sm mb-4 md:mb-0 md:mr-4 md:sticky md:top-4 md:h-[calc(100vh-2rem)] md:max-h-[calc(100vh-2rem)]"
      style={isMobile ? {} : { width: `${sidebarWidth}px`, minWidth: `${sidebarWidth}px`, maxWidth: `${sidebarWidth}px` }}
    >
      <div className="mb-4">
        <p className="sticky top-0 text-slate-800 bg-white z-10 text-base md:block hidden font-bold mb-3 px-4 text-left">
          Categories
        </p>
        <CategorySearchBar
          searchQuery={categorySearchQuery}
          setSearchQuery={setCategorySearchQuery}
        />
      </div>

      {categorySearchQuery && (
        <div className="mb-2 text-sm text-gray-500 px-4">
          Found {filteredCategories.length} categories
        </div>
      )}

      <div className="px-4 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 overflow-x-hidden">
        <CategoriesList
          filteredCategories={filteredCategories}
          onCategorySelect={handleCategorySelection}
          activeCategory={activeCategory}
          totalCategoriesCount={categories.length}
          navZone={navZone}
          navCategoryIndex={navCategoryIndex}
          setNavCategoryIndex={setNavCategoryIndex}
          setNavZone={setNavZone}
        />
      </div>

      {/* Resizer Handle */}
      {!isMobile && (
        <div
          onMouseDown={startResizing}
          className="absolute top-0 right-0 w-2 h-full cursor-col-resize select-none hover:bg-teal-500/10 active:bg-teal-600/20 transition-colors z-20 group"
          title="Drag to resize category section"
        >
          <div className="absolute right-[3px] top-1/2 -translate-y-1/2 w-[2px] h-8 bg-gray-300 rounded group-hover:bg-teal-500 transition-colors" />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
